import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { useUser } from '@clerk/clerk-react';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const [activeTab, setActiveTab] = useState('portraits');
  const [isLoading, setIsLoading] = useState(true);
  
  // Mock data for user's subscription and portraits
  const [subscription, setSubscription] = useState({
    plan: 'Premium',
    status: 'active',
    nextBillingDate: '2023-12-01',
    price: '35'
  });
  
  const [portraits, setPortraits] = useState([
    {
      id: 'p1',
      name: 'Family Portrait',
      image: 'https://placehold.co/300x400/6A3EA1/FFFFFF?text=Portrait',
      status: 'active',
      lastInteraction: '2 hours ago'
    },
    {
      id: 'p2',
      name: 'Wizard Self',
      image: 'https://placehold.co/300x400/9857D3/FFFFFF?text=Portrait',
      status: 'training',
      lastInteraction: 'In training'
    }
  ]);
  
  useEffect(() => {
    // Simulate loading data
    if (isLoaded) {
      setTimeout(() => {
        setIsLoading(false);
      }, 800);
    }
  }, [isLoaded]);
  
  // Redirect if not authenticated
  useEffect(() => {
    if (isLoaded && !user) {
      navigate('/');
    }
  }, [isLoaded, user, navigate]);
  
  if (!isLoaded || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-20 h-20 bg-purple-200 rounded-full mb-4"></div>
            <div className="h-4 w-48 bg-purple-200 rounded mb-2"></div>
            <div className="h-3 w-32 bg-purple-100 rounded"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  const renderTabContent = () => {
    switch (activeTab) {
      case 'portraits':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portraits.map(portrait => (
              <motion.div
                key={portrait.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-xl overflow-hidden shadow-lg"
              >
                <div className="relative">
                  <img 
                    src={portrait.image} 
                    alt={portrait.name} 
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <h3 className="text-white font-medium text-lg">{portrait.name}</h3>
                  </div>
                  <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${
                    portrait.status === 'active' ? 'bg-green-100 text-green-800' : 
                    portrait.status === 'training' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {portrait.status.charAt(0).toUpperCase() + portrait.status.slice(1)}
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-500">Last activity: {portrait.lastInteraction}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors"
                      onClick={() => alert(`Interacting with ${portrait.name}`)}
                    >
                      Interact
                    </button>
                    <button 
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition-colors"
                      onClick={() => alert(`Settings for ${portrait.name}`)}
                    >
                      Settings
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 flex flex-col items-center justify-center h-[24rem] cursor-pointer hover:bg-gray-200 transition-colors"
              onClick={() => alert('Create new portrait')}
            >
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 className="text-purple-600 font-medium text-lg">Create New Portrait</h3>
              <p className="text-gray-500 text-sm mt-2 text-center max-w-[200px]">
                Add a new magical portrait to your collection
              </p>
            </motion.div>
          </div>
        );
      case 'account':
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-serif font-semibold mb-6 text-purple-800">Account Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-medium mb-4 text-gray-800">Personal Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      {user.fullName || 'Not provided'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      {user.primaryEmailAddress?.emailAddress || 'Not provided'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Member Since</label>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      {new Date(user.createdAt).toLocaleDateString() || 'Not available'}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <button 
                    className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors"
                    onClick={() => alert('Edit profile')}
                  >
                    Edit Profile
                  </button>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium mb-4 text-gray-800">Subscription Details</h3>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Current Plan:</span>
                    <span className="text-purple-700 font-medium">{subscription.plan}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Status:</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Price:</span>
                    <span>${subscription.price}/month</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Next Billing:</span>
                    <span>{new Date(subscription.nextBillingDate).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <button 
                    className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 py-2 px-4 rounded-lg transition-colors"
                    onClick={() => navigate('/pricing')}
                  >
                    Upgrade Plan
                  </button>
                  <button 
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition-colors"
                    onClick={() => alert('Cancel subscription')}
                  >
                    Cancel Subscription
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'billing':
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-serif font-semibold mb-6 text-purple-800">Billing History</h2>
            
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg mb-6">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Date</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Amount</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <tr>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">Nov 1, 2023</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Subscription - {subscription.plan} Plan</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${subscription.price}.00</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Paid
                      </span>
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium">
                      <button className="text-purple-600 hover:text-purple-900">Receipt</button>
                    </td>
                  </tr>
                  <tr>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">Oct 1, 2023</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">Subscription - {subscription.plan} Plan</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${subscription.price}.00</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Paid
                      </span>
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium">
                      <button className="text-purple-600 hover:text-purple-900">Receipt</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="mt-4">
              <h3 className="text-lg font-medium mb-4 text-gray-800">Payment Method</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4 flex items-center">
                <div className="flex-shrink-0 mr-4 bg-white p-2 rounded shadow">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Visa ending in 4242</p>
                  <p className="text-sm text-gray-500">Expires 12/25</p>
                </div>
              </div>
              
              <button 
                className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors"
                onClick={() => alert('Update payment method')}
              >
                Update Payment Method
              </button>
            </div>
          </div>
        );
      case 'help':
        return (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-serif font-semibold mb-6 text-purple-800">Help & Support</h2>
            
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-4 text-gray-800">Frequently Asked Questions</h3>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-700 mb-2">How do I create a new portrait?</h4>
                  <p className="text-gray-600">Go to the Portraits tab and click on "Create New Portrait". Follow the instructions to upload photos and customize your magical portrait.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-700 mb-2">How long does portrait creation take?</h4>
                  <p className="text-gray-600">Portrait creation typically takes 24-48 hours depending on the complexity and current system load.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-700 mb-2">Can I change my subscription plan?</h4>
                  <p className="text-gray-600">Yes, you can upgrade or downgrade your plan at any time from the Account tab. Changes will take effect on your next billing cycle.</p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-4 text-gray-800">Contact Support</h3>
              <p className="text-gray-600 mb-4">Need additional help? Our support team is available 24/7.</p>
              <div className="flex space-x-4">
                <button 
                  className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors"
                  onClick={() => alert('Contact support')}
                >
                  Contact Support
                </button>
                <button 
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition-colors"
                  onClick={() => navigate('/information?section=faq')}
                >
                  View All FAQs
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-bold text-gray-900">
            Hello, {user.firstName || user.username || 'Wizard'}!
          </h1>
          <p className="text-gray-600 mt-2">
            Welcome to your magical dashboard. Manage your portraits and account.
          </p>
        </div>
        

        {/* temporary message We will reach out as soon as your app is ready */}
        
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0a6 6 0 11-12 0 6 6 0 0112 0zM9 11a1 1 0 100-2 1 1 0 000 2zm3-1a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-700">
                Thank you for your purchase! We just refunded your card because we are at capacity. We will reach out as soon as possible to get you your portraits.
              </p>
              <p className="text-sm text-yellow-700"> 
                In case of any questions, please contact us at <a href="mailto:support@wizardportraits.com" className="text-yellow-700 underline">support@wizardportraits.com</a>
              </p>
            </div>
          </div>
        </div>


        {/* Dashboard summary cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow p-6 border-t-4 border-purple-600"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-medium text-gray-900">Active Portraits</h2>
                <p className="mt-1 text-3xl font-semibold text-purple-700">{portraits.filter(p => p.status === 'active').length}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="bg-white rounded-xl shadow p-6 border-t-4 border-amber-500"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-amber-100 text-amber-600 mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-medium text-gray-900">Current Plan</h2>
                <p className="mt-1 text-xl font-semibold text-amber-700">{subscription.plan}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="bg-white rounded-xl shadow p-6 border-t-4 border-green-600"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-medium text-gray-900">Next Billing</h2>
                <p className="mt-1 text-xl font-semibold text-green-700">{new Date(subscription.nextBillingDate).toLocaleDateString()}</p>
              </div>
            </div>
          </motion.div>
        </div> */}
        
        {/* Tabs navigation */}
        {/* <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('portraits')}
              className={`pb-4 px-1 font-medium text-sm ${
                activeTab === 'portraits'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent'
              }`}
            >
              Portraits
            </button>
            <button
              onClick={() => setActiveTab('account')}
              className={`pb-4 px-1 font-medium text-sm ${
                activeTab === 'account'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent'
              }`}
            >
              Account
            </button>
            <button
              onClick={() => setActiveTab('billing')}
              className={`pb-4 px-1 font-medium text-sm ${
                activeTab === 'billing'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent'
              }`}
            >
              Billing
            </button>
            <button
              onClick={() => setActiveTab('help')}
              className={`pb-4 px-1 font-medium text-sm ${
                activeTab === 'help'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent'
              }`}
            >
              Help & Support
            </button>
          </nav>
        </div> */}
        
        {/* Tab content */}
        {/* <div>{renderTabContent()}</div> */}
      </main>
      
      <Footer />
    </div>
  );
} 